import React, { useMemo } from 'react';

import { Box, Flex, Image } from '@chakra-ui/react';
import { Button, Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@mesh.ai/rabbit';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';
import store from 'store';
import { check1on1V2Enabled } from 'utils/helpers/featureFlags';
import {
  checkGoalModuleEnabled,
  checkOneOnOneModuleEnabled,
  checkFeedbackModuleEnabled,
} from 'utils/helpers/moduleAccess';

import BullsEye from 'assets/assetsV1/illustrations/deerIllustrations/bullseye.svg';
import ColdNudges from 'assets/assetsV1/illustrations/deerIllustrations/coldNudges.svg';
import ConcentricPattern1 from 'assets/assetsV1/illustrations/deerIllustrations/concentricPattern1.png';
import ConcentricPattern2 from 'assets/assetsV1/illustrations/deerIllustrations/concentricPattern2.png';
import ConcentricPattern3 from 'assets/assetsV1/illustrations/deerIllustrations/concentricPattern3.png';
import JigSaw from 'assets/assetsV1/illustrations/deerIllustrations/jigsaw.svg';
import ThumbsUp from 'assets/assetsV1/illustrations/deerIllustrations/thumbsUp.svg';

import { FEEDBACK_FORM_TYPE, OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { pushFeedbackParams } from 'constants/helper';
import { sendClickedOnColdStartNudge } from 'constants/mixpanelHelper';

import { TextBodyLarge, TextBodyMedium } from 'uiToolkitV1/Typography';

const BG_IMAGES = [ConcentricPattern1, ConcentricPattern2, ConcentricPattern3];

const ColdStartCard = ({
  title,
  description,
  icon,
  buttonText,
  onClick,
  isButtonDisabled,
  disabledReason,
  index,
}: {
  title: string;
  description: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  buttonText: string;
  onClick: () => void;
  isButtonDisabled: boolean;
  disabledReason: string;
  index: number;
}) => (
  <Box
    display="flex"
    flexDir="column"
    bg="white"
    boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
    borderRadius="8px"
    padding="8px"
    flex="1"
    position="relative"
  >
    <Box p="8px" mb="12px">
      <Flex alignItems="center" mb="4px">
        <Image src={icon} width="24px" mr="4px" />
        <TextBodyLarge bold>{title.split(' ')[0]}</TextBodyLarge> &nbsp;
        <TextBodyLarge>{title.split(' ')[1]}</TextBodyLarge>
      </Flex>
      <TextBodyMedium color="text.g2">{description}</TextBodyMedium>
    </Box>
    <Box mt="auto" p="0px 10px" zIndex={2}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button onClick={onClick} disabled={isButtonDisabled}>
              {buttonText}
            </Button>
          </TooltipTrigger>
          {isButtonDisabled && <TooltipContent>{disabledReason}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    </Box>
    <Image
      position="absolute"
      src={BG_IMAGES[index]}
      width="100%"
      height="100px"
      alt="concentric-bg"
      bottom="0"
      zIndex={1}
      userSelect="none"
    />
  </Box>
);

const ColdStartMeshRecommends = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const employeeId = useSelector(selectLoggedInEmployeeId);

  const { keyResultsName } = useOrganisationSettings();

  const COLD_START_DATA = useMemo(
    () => [
      {
        title: 'Set clearly',
        description: `Define your success path through smart ${modifyString(
          keyResultsName,
          StringModifier.PLURAL_SENTENCE_CASE
        )} and personalized competencies. Focus on what you want to achieve and get the tools to map your progress effectively.`,
        icon: BullsEye,
        buttonText: `Create ${modifyString(keyResultsName, StringModifier.SENTENCE_CASE)}`,
        onClick: () => {
          const { selectedTimelines } = store.getState(okrsPageSelector);
          sendClickedOnColdStartNudge({
            eventProperties: { type: 'Goals' },
          });
          dispatch(
            // @ts-expect-error error :(
            openCreateOKRPanel({
              selectedObjective: null,
              selectedTimeBoundPeriod: selectedTimelines?.[0],
              okrPanelType: OKR_TYPES.KEY_RESULT,
              onKeyResultCreation: () => {},
              onObjectiveCreation: () => {},
              history,
              location,
            })
          );
        },
        isButtonDisabled: !checkGoalModuleEnabled(),
        disabledReason: 'Not enabled for your organisation, talk to your Mesh administrator',
      },
      {
        title: 'Coach frequently',
        description:
          'Boost your performance with regular feedback and guidance during 1:1s and check-ins. These provide an opportunity to refine your skills and stay aligned with your team’s objectives.',
        icon: JigSaw,
        buttonText: 'Setup 1:1',
        onClick: () => {
          sendClickedOnColdStartNudge({
            eventProperties: { type: 'One on one' },
          });
          if (check1on1V2Enabled()) {
            dispatch(
              // @ts-expect-error ts issue
              openCreateOneOnOneDrawer({
                excludedParticipantIds: [],
              })
            );
          }
        },
        isButtonDisabled: !checkOneOnOneModuleEnabled(),
        disabledReason: 'Not enabled for your organisation, talk to your Mesh administrator',
      },
      {
        title: 'Assess fairly',
        description:
          'Benefit from unbiased reviews and feedback to ensure that your contributions are recognized fairly. This helps you with your career progression.',
        icon: ThumbsUp,
        buttonText: 'Feedback',
        onClick: () => {
          // @ts-expect-error ts issue
          sendClickedOnColdStartNudge({
            eventProperties: { type: 'Feedback' },
          });
          pushFeedbackParams({
            history,
            formType: FEEDBACK_FORM_TYPE.REQUESTED,
            newForm: true,
            empId: employeeId ?? null,
          });
        },
        isButtonDisabled: !checkFeedbackModuleEnabled(),
        disabledReason: 'Not enabled for your organisation, talk to your Mesh administrator',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkGoalModuleEnabled, checkOneOnOneModuleEnabled, checkFeedbackModuleEnabled]
  );
  return (
    <Box bg="white" boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)" borderRadius="8px" padding="18px" overflow="auto">
      <Box p="8px" mb="8px">
        <TextBodyLarge bold mb="4px">
          Welcome to Mesh.ai!
        </TextBodyLarge>
        <TextBodyMedium color="text.g2">
          A performance enablement platform designed to accelerate your growth and enhance your team&apos;s
          effectiveness. Discover how it empowers you to achieve your career goals through tailored benefits and support
          mechanisms that drive success.
        </TextBodyMedium>
      </Box>
      <Box boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)" borderRadius="8px" mb="16px">
        {/* @ts-expect-error src expects a string */}
        <Image src={ColdNudges} width="100%" height="220px" alt="Welcome to Mesh.ai!" objectFit="contain !important" />
      </Box>
      <Flex gap="16px" w="100%" direction={{ base: 'column', lg: 'column', xl: 'row' }}>
        {COLD_START_DATA.map((card, index) => (
          <ColdStartCard
            key={card.title}
            title={card.title}
            description={card.description}
            icon={card.icon}
            buttonText={card.buttonText}
            onClick={card.onClick}
            isButtonDisabled={card.isButtonDisabled}
            disabledReason={card.disabledReason}
            index={index}
          />
        ))}
      </Flex>
    </Box>
  );
};

export { ColdStartMeshRecommends };
